import { get } from "svelte/store"
import { postDBEntry } from "../../../../api"
import { login } from "../../../store"
import ExifReader from "exifreader"

export async function getLocation(tags: ExifReader.Tags) {
    let latitude = tags.GPSLatitude?.description || ""
    let longitude = tags.GPSLongitude?.description || ""

    if (!latitude || !longitude) {
        const location = get(locationStore)
        latitude = location.latitude
        longitude = location.longitude
    }

    return {
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
    }
}

export async function saveMediaEntry(
    file: File,
    fileData: string | ArrayBuffer,
    datetime: string | null,
    location: { latitude: number | null; longitude: number | null },
    taskId: string
) {
    const loginObj = get(login)
    const res = await postDBEntry("media", {
        file: {
            path: file.name,
            type: file.type,
            size: file.size,
            src: typeof fileData === "string" ? fileData : "",
        },
        shotAt: datetime ? new Date(datetime) : new Date(),
        comment: "",
        uploadedAt: new Date(),
        location,
        attachedTask: taskId,
        author: {
            id: loginObj.user.id,
        },
    })

    return res.data as Media
}

export async function processImage(
    img: HTMLImageElement,
    tags: any,
    file: File,
    resolve: Function,
    reject: Function,
    location: any,
    datetime: string | null,
    taskId: string
) {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    if (!ctx) {
        reject(new Error("Could not get canvas context"))
        return
    }

    // Maximalbreite von 1000 Pixel einstellen
    const maxWidth = 1000
    let width = img.width,
        height = img.height

    // Berechne das Seitenverhältnis und passe die Höhe entsprechend an
    if (width > maxWidth) {
        height = Math.round((maxWidth / width) * height)
        width = maxWidth
    }

    canvas.width = width
    canvas.height = height

    // Zeichne das Bild mit der angepassten Größe
    ctx.drawImage(img, 0, 0, width, height)

    // Komprimiere das Bild und konvertiere es zu JPEG mit 60% Qualität
    const compressedBlob = await compressImage(canvas, file)

    if (!compressedBlob) {
        reject(new Error("Canvas conversion to compressed blob failed"))
        return
    }

    // Speichere das komprimierte Bild als neues File-Objekt
    const compressedFile = new File([compressedBlob], file.name.replace(/\.[^.]+$/, ".jpg"), {
        type: "image/jpeg",
        lastModified: Date.now(),
    })

    const readerWithoutExif = new FileReader()
    readerWithoutExif.onload = async (event: ProgressEvent<FileReader>) => {
        const processedData = event.target?.result as string
        if (processedData) {
            const entry = await saveMediaEntry(compressedFile, processedData, datetime, location, taskId)
            resolve(entry)
        }
    }
    readerWithoutExif.readAsDataURL(compressedFile)
}
import imageCompression from "browser-image-compression"
import { locationStore } from "../../widgets/location/locationStore"

export async function compressImage(canvas: HTMLCanvasElement, file: File): Promise<Blob> {
    const dataUrl = canvas.toDataURL("image/jpeg", 0.6) // Komprimiere mit 60% Qualität
    const imageBlob = await fetch(dataUrl).then((res) => res.blob())

    // Nutze die browser-image-compression Library zur weiteren Kompression
    const options = {
        maxSizeMB: 1, // Optional, falls die Datei noch kleiner sein soll
        maxWidthOrHeight: 1000, // Maximal 1000px Breite oder Höhe
        useWebWorker: true, // Web-Worker verwenden, um Performance zu verbessern
    }

    const compressedFile = new File([imageBlob], file.name, {
        type: file.type,
        lastModified: file.lastModified,
    })

    return imageCompression(compressedFile, options)
}

export async function readInput(input: HTMLInputElement, taskId: string): Promise<Promise<Media>[]> {
    const filePromises = Array.from(input.files).map(
        (file) =>
            new Promise<Media>(async (resolve, reject) => {
                const reader = new FileReader()
                reader.onload = async (e: ProgressEvent<FileReader>) => {
                    try {
                        const fileData = e.target?.result as string
                        const tags = ExifReader.load(fileData)
                        const location = await getLocation(tags)
                        const datetime = tags.DateTime?.description || null

                        const img = new Image()
                        img.src = fileData
                        img.onload = () => processImage(img, tags, file, resolve, reject, location, datetime, taskId)
                        img.onerror = () => reject(new Error("Image loading failed"))
                    } catch (error) {
                        reject(error)
                    }
                }
                reader.readAsDataURL(file)
            })
    )
    return filePromises
}

export function adjustCanvasForOrientation(canvas: HTMLCanvasElement, img: HTMLImageElement, orientation: number) {
    let width = img.width,
        height = img.height
    if (orientation > 4) {
        canvas.width = height
        canvas.height = width
    } else {
        canvas.width = width
        canvas.height = height
    }
    return [width, height]
}

export async function handleProcessedBlob(
    blob: Blob,
    file: File,
    location: any,
    datetime: string | null,
    resolve: Function
) {
    const strippedFile = new File([blob], file.name, {
        type: file.type,
        lastModified: Date.now(),
    })

    const readerWithoutExif = new FileReader()
    readerWithoutExif.onload = async (event: ProgressEvent<FileReader>) => {
        const processedData = event.target?.result as string
        if (processedData) {
            const entry = await saveMediaEntry(strippedFile, processedData, datetime, location)
            resolve(entry)
        }
    }
    readerWithoutExif.readAsDataURL(strippedFile)
}
